/**
 * The experiments list toolbar: a name search plus one dropdown per facet
 * (agent, dataset, status, creator, evaluator). Every choice lives in the URL,
 * so a filtered list is a shareable link. The list API returns every
 * experiment, so filtering happens here and the options come from the rows.
 */

import { Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useUrlState } from "@/lib/use-url-state";
import { isLive } from "./status";
import type { ExperimentStatus, ExperimentSummary } from "./types";
import { Select } from "@/components/ui/select";

/** The URL keys, in toolbar order. `agent=none` means recorded outputs (no agent). */
const KEYS = ["q", "agent", "dataset", "status", "by", "evaluator"] as const;
type Key = (typeof KEYS)[number];
export type ExperimentFilters = Record<Key, string>;

export const NO_AGENT = "none";

const STATUSES: Array<{ key: ExperimentStatus; label: string }> = [
  { key: "running", label: "Running" },
  { key: "completed", label: "Completed" },
  { key: "stopped", label: "Stopped" },
  { key: "failed", label: "Failed" },
  { key: "draft", label: "Draft" },
];

export function useExperimentFilters() {
  const url = useUrlState();
  const filters = Object.fromEntries(KEYS.map((k) => [k, url.get(k) ?? ""])) as ExperimentFilters;
  const active = KEYS.some((k) => filters[k] !== "");
  const set = (key: Key, value: string) => url.set({ [key]: value }, { replace: key === "q" });
  const clear = () => url.set(Object.fromEntries(KEYS.map((k) => [k, null])));
  return { filters, active, set, clear };
}

/** "Running" covers every live state (stopping, finishing), as the pill does. */
function statusMatches(e: ExperimentSummary, status: string): boolean {
  return status === "running" ? isLive(e.status) : e.status === status;
}

export function applyFilters(rows: ExperimentSummary[], f: ExperimentFilters): ExperimentSummary[] {
  const q = f.q.trim().toLowerCase();
  return rows.filter(
    (e) =>
      (!q || e.name.toLowerCase().includes(q)) &&
      (!f.agent || (e.agent?.id ?? NO_AGENT) === f.agent) &&
      (!f.dataset || e.dataset?.id === f.dataset) &&
      (!f.status || statusMatches(e, f.status)) &&
      (!f.by || e.created_by === f.by) &&
      (!f.evaluator || e.evaluators.some((v) => v.key === f.evaluator)),
  );
}

type Option = [value: string, label: string];

/** Distinct options, sorted by label. */
function options(pairs: Option[]): Option[] {
  return [...new Map(pairs).entries()].sort((a, b) => a[1].localeCompare(b[1]));
}

function Facet({
  label,
  all,
  value,
  items,
  onChange,
}: {
  label: string;
  all: string;
  value: string;
  items: Option[];
  onChange: (value: string) => void;
}) {
  return (
    <Select
      label={label}
      className="max-w-[200px]"
      buttonClassName={value ? "border-ink/40 font-medium text-ink" : "text-muted"}
      value={value}
      onChange={onChange}
      options={[{ value: "", label: all }, ...items.map(([v, l]) => ({ value: v, label: l }))]}
    />
  );
}

export function ExperimentsToolbar({
  experiments,
  state,
}: {
  experiments: ExperimentSummary[];
  state: ReturnType<typeof useExperimentFilters>;
}) {
  const { filters: f, active, set, clear } = state;
  const agents = options(
    experiments.map((e) => [e.agent?.id ?? NO_AGENT, e.agent?.name ?? "Recorded outputs"]),
  );
  const datasets = options(
    experiments.flatMap((e) => (e.dataset ? [[e.dataset.id, e.dataset.name] as Option] : [])),
  );
  const people = options(experiments.map((e) => [e.created_by, e.created_by]));
  const evaluators = options(experiments.flatMap((e) => e.evaluators.map((v): Option => [v.key, v.name])));

  return (
    <div className="flex flex-wrap items-center gap-2" role="search" aria-label="Filter experiments">
      <div className="relative">
        <Search
          size={14}
          strokeWidth={2}
          className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-dim"
          aria-hidden
        />
        <input
          className="inp w-60 pl-8"
          type="search"
          placeholder="Search by name"
          aria-label="Search experiments"
          value={f.q}
          onChange={(e) => set("q", e.target.value)}
        />
      </div>
      <Facet
        label="Agent"
        all="All agents"
        value={f.agent}
        items={agents}
        onChange={(v) => set("agent", v)}
      />
      <Facet
        label="Dataset"
        all="All datasets"
        value={f.dataset}
        items={datasets}
        onChange={(v) => set("dataset", v)}
      />
      <Facet
        label="Status"
        all="Any status"
        value={f.status}
        items={STATUSES.map((s) => [s.key, s.label])}
        onChange={(v) => set("status", v)}
      />
      <Facet label="Created by" all="Anyone" value={f.by} items={people} onChange={(v) => set("by", v)} />
      {evaluators.length > 0 && (
        <Facet
          label="Evaluator"
          all="All evaluators"
          value={f.evaluator}
          items={evaluators}
          onChange={(v) => set("evaluator", v)}
        />
      )}
      {active && (
        <Button variant="ghost" size="sm" className="text-muted" onClick={clear}>
          <X size={13} strokeWidth={2.2} aria-hidden />
          Clear
        </Button>
      )}
    </div>
  );
}
